// src/modules/settings/helpers/remoteAccessSettings.ts
import {
  FeatureListEntry,
  SplashtopRemoteAccessSettings,
  getIgnoreCase,
  getSplashtopSettings,
  withSplashtopSettings,
} from '../../../components/services/TenantSettingsService';

export type RemoteAccessClientType = 'app' | 'web';

/** The Splashtop entry as the settings form edits it. */
export interface RemoteAccessFormValues {
  enabled: boolean;
  /** Streamer req_perm: 0 no approval, 1 ask/reject on timeout, 2 ask/allow on timeout, 3 ask/reject (allow at login screen). */
  requestPermission: number;
  /** 1 pop-up, 2 banner, 3 banner + pop-up. */
  notificationMode: number;
  allowCloseBanner: boolean;
  hideTrayIcon: boolean;
  idleTimeoutMinutes: number | string;
  clientType: RemoteAccessClientType;
  /** Off = every group (saved as an empty groupIds list). */
  limitToGroups: boolean;
  groupIds: string[];
  permissions: Record<string, boolean>;
}

export interface TenantGroupOption {
  id: string;
  name: string;
}

export const REQUEST_PERMISSION_OPTIONS = [
  { value: 0, label: 'No approval needed', description: 'Sessions connect straight away.' },
  {
    value: 3,
    label: 'Ask the user (allow at the login screen)',
    description: 'The signed-in user must accept. No answer rejects the session. With nobody signed in, the session is allowed.',
  },
  {
    value: 1,
    label: 'Ask the user (always required)',
    description: 'The signed-in user must accept. No answer rejects the session, and so does the login screen (no unattended access).',
  },
  {
    value: 2,
    label: 'Ask the user (allow if no answer)',
    description: 'The user can reject the session. No answer allows it.',
  },
];

export const NOTIFICATION_MODE_OPTIONS = [
  { value: 3, label: 'Banner and pop-up' },
  { value: 2, label: 'Banner' },
  { value: 1, label: 'Pop-up' },
];

export const CLIENT_TYPE_OPTIONS: { value: RemoteAccessClientType; label: string; description: string }[] = [
  {
    value: 'app',
    label: 'Splashtop for RMM app',
    description: 'Opens the Splashtop for RMM viewer, which must be installed on the admin’s computer.',
  },
  { value: 'web', label: 'Web browser', description: 'Opens the session in a browser tab. Nothing to install.' },
];

const PERMISSION_LABELS: Record<string, string> = {
  file_transfer: 'File transfer',
  copy_paste: 'Copy and paste',
  remote_print: 'Remote print',
  keystroke: 'Send keystrokes (Ctrl+Alt+Del and shortcuts)',
  task_manager: 'Task manager',
  service_manager: 'Service manager',
  registry_editor: 'Registry editor',
  device_manager: 'Device manager',
};

export const permissionLabel = (name: string): string =>
  PERMISSION_LABELS[name] ?? name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const requestPermissionLabel = (value: number): string =>
  REQUEST_PERMISSION_OPTIONS.find((o) => o.value === value)?.label ?? REQUEST_PERMISSION_OPTIONS[0].label;

export const notificationModeLabel = (value: number): string =>
  NOTIFICATION_MODE_OPTIONS.find((o) => o.value === value)?.label ?? NOTIFICATION_MODE_OPTIONS[0].label;

export const clientTypeLabel = (value: RemoteAccessClientType): string => CLIENT_TYPE_OPTIONS.find((o) => o.value === value)!.label;

const toNumber = (value: unknown, fallback: number): number => {
  const n = Number(value);
  return value !== null && value !== '' && Number.isFinite(n) ? Math.trunc(n) : fallback;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/**
 * Form values for the tenant's Splashtop entry, or for the defaults when it has none. A setting missing from an
 * existing entry gets the value AgentAPI uses for it (RemoteAccessSettingsParser), so the form shows what is in effect.
 * Default permission names missing from the entry are shown as on, which is what Splashtop does when one isn't sent.
 */
export const toRemoteAccessForm = (
  featureList: FeatureListEntry[] | null | undefined,
  defaults: SplashtopRemoteAccessSettings,
): RemoteAccessFormValues => {
  const stored = getSplashtopSettings(featureList);
  const source: Record<string, unknown> = stored ?? JSON.parse(JSON.stringify(defaults));
  const get = (name: string) => getIgnoreCase(source, name);

  const requestPermission = Math.min(3, Math.max(0, toNumber(get('requestPermission'), 0)));
  const notificationMode = toNumber(get('notificationMode'), 3);
  const groupIds = Array.isArray(get('groupIds')) ? (get('groupIds') as unknown[]).map(String) : [];

  const permissions: Record<string, boolean> = {};
  for (const [name, value] of Object.entries(asRecord(get('permissions')))) {
    if (name.trim() !== '') permissions[name] = toNumber(value, 1) !== 0;
  }
  for (const name of Object.keys(asRecord(defaults.permissions))) {
    if (!(name in permissions)) permissions[name] = true;
  }

  return {
    enabled: get('enabled') === true,
    requestPermission,
    notificationMode: notificationMode >= 1 && notificationMode <= 3 ? notificationMode : 3,
    allowCloseBanner: toNumber(get('allowCloseBanner'), 0) !== 0,
    hideTrayIcon: get('hideTrayIcon') === true,
    idleTimeoutMinutes: Math.max(0, toNumber(get('idleTimeoutMinutes'), 0)),
    clientType: String(get('clientType') ?? '').toLowerCase() === 'web' ? 'web' : 'app',
    limitToGroups: groupIds.length > 0,
    groupIds,
    permissions,
  };
};

/** The Splashtop settings to store, in the shape AgentAPI reads (numbers 0/1 for flags it reads as int). */
export const toSplashtopChanges = (form: RemoteAccessFormValues): Record<string, unknown> => ({
  enabled: form.enabled,
  requestPermission: form.requestPermission,
  notificationMode: form.notificationMode,
  allowCloseBanner: form.allowCloseBanner ? 1 : 0,
  hideTrayIcon: form.hideTrayIcon,
  idleTimeoutMinutes: Math.max(0, toNumber(form.idleTimeoutMinutes, 0)),
  clientType: form.clientType,
  groupIds: form.limitToGroups ? [...form.groupIds] : [],
  permissions: Object.fromEntries(Object.entries(form.permissions).map(([name, on]) => [name, on ? 1 : 0])),
});

const comparable = (form: RemoteAccessFormValues): string => {
  const changes = toSplashtopChanges(form);
  const permissions = changes.permissions as Record<string, number>;
  return JSON.stringify({
    ...changes,
    groupIds: (changes.groupIds as string[]).map((id) => id.toLowerCase()).sort(),
    permissions: Object.keys(permissions)
      .sort()
      .map((name) => [name, permissions[name]]),
  });
};

/**
 * The featureList to save, or undefined when nothing in the Splashtop entry changed. Built from the list that was
 * loaded, so other features in it stay untouched. Never saves when the list couldn't be loaded (null): the list is
 * saved whole, so saving it then would wipe the tenant's other features.
 */
export const buildRemoteAccessFeatureList = (
  originalFeatureList: FeatureListEntry[] | null,
  form: RemoteAccessFormValues | undefined,
  defaults: SplashtopRemoteAccessSettings,
): FeatureListEntry[] | undefined => {
  if (originalFeatureList === null || !form) return undefined;
  if (comparable(form) === comparable(toRemoteAccessForm(originalFeatureList, defaults))) return undefined;
  return withSplashtopSettings(originalFeatureList, toSplashtopChanges(form), defaults);
};

/** Tenant groups from GET tenantservice/TenantGroup/{tenantId}/groups. */
export const toTenantGroupOptions = (response: unknown): TenantGroupOption[] => {
  const data = getIgnoreCase(asRecord(response), 'data');
  const tenant = Array.isArray(data) ? asRecord(data[0]) : {};
  const groups = getIgnoreCase(tenant, 'tenantGroupList');
  return (Array.isArray(groups) ? groups : [])
    .map((g) => asRecord(g))
    .filter((g) => g.tenantGroupId)
    .map((g) => ({ id: String(g.tenantGroupId), name: String(g.groupName ?? g.tenantGroupId) }))
    .sort((a, b) => a.name.localeCompare(b.name));
};
